import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { CheckCircleFilled, CloseOutlined, CopyOutlined, ShareAltOutlined, LockOutlined } from '@ant-design/icons';
import QRCode from 'qrcode';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import { VisitAccessController } from './interfaces';

export const VisitAccessSuccess: React.FC<{ controller: VisitAccessController }> = ({ controller }) => {
    const { translate } = useTranslator();
    const t = (key: string) => translate({ key });
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [copied, setCopied] = useState(false);
    const created = controller.created;
    const point = controller.selectedPoint;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas && created?.kind === 'reception' && created.res.qr_payload) {
            QRCode.toCanvas(canvas, created.res.qr_payload, {
                width: 544, // backing-store alto (retina); el tamaño visual se fija abajo
                margin: 4, // quiet zone: sin ella el lector no detecta los patrones
                errorCorrectionLevel: 'L', // pantalla, no impreso: L reduce los módulos
                color: { dark: '#000000', light: '#ffffff' },
            })
                .then(() => {
                    canvas.style.width = 'min(240px, 68vw)';
                    canvas.style.height = 'min(240px, 68vw)';
                })
                .catch(() => undefined);
        }
    }, [created]);

    if (!created) {
        return null;
    }

    const pointLabel = point
        ? point.kind === 'reception'
            ? `${point.site_name} · ${t('visit-access.picker-reception')}`
            : `${point.site_name} · ${point.name}`
        : '';

    const onCopy = () => {
        const text = created.kind === 'keypad' ? created.res.code : created.res.share_url;
        navigator.clipboard?.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        });
    };

    return (
        <div className={'va-success'}>
            <div className={'va-success-nav'}>
                <CheckCircleFilled aria-hidden className={'va-success-check'} />
                <span className={'va-success-title'}>{t('visit-access.success-title')}</span>
                <button
                    className={'va-success-close'}
                    aria-label={t('visit-access.close')}
                    onClick={controller.onClose}
                >
                    <CloseOutlined aria-hidden />
                </button>
            </div>

            <div className={'va-success-point'}>
                <LockOutlined aria-hidden /> <span>{pointLabel}</span>
            </div>

            {created.kind === 'keypad' ? (
                <>
                    <div className={'va-success-codebox'}>
                        <div className={'va-success-codelabel'}>{t('visit-access.success-code-label')}</div>
                        <div className={'va-success-code'}>{created.res.code}</div>
                        <div className={'va-success-valid'}>{controller.validityLabel}</div>
                        <Button className={'va-success-copy'} icon={<CopyOutlined />} onClick={onCopy}>
                            {copied ? t('visit-access.success-copied') : t('visit-access.success-copy')}
                        </Button>
                    </div>
                    <div className={'va-success-hint'}>{t('visit-access.success-hint-code')}</div>
                    <div className={'va-success-cta'}>
                        <div className={'va-success-foot'}>{t('visit-access.success-foot-code')}</div>
                        <Button
                            type={'primary'}
                            className={'va-cta-btn'}
                            icon={<ShareAltOutlined />}
                            onClick={controller.onShare}
                        >
                            {t('visit-access.success-share-code')}
                        </Button>
                        <Button type={'link'} className={'va-success-secondary'} onClick={controller.onShareCodeOnly}>
                            {t('visit-access.success-share-code-only')}
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <div className={'va-success-qrbox'}>
                        <canvas
                            ref={canvasRef}
                            className={'va-success-qr'}
                            width={544}
                            height={544}
                            style={{ width: 'min(240px, 68vw)', height: 'min(240px, 68vw)' }}
                        />
                        <div className={'va-success-valid'}>{controller.validityLabel}</div>
                    </div>
                    <div className={'va-success-hint'}>{t('visit-access.success-hint-qr')}</div>
                    <div className={'va-success-cta'}>
                        <div className={'va-success-foot'}>{t('visit-access.success-foot-qr')}</div>
                        <Button
                            type={'primary'}
                            className={'va-cta-btn'}
                            icon={<ShareAltOutlined />}
                            onClick={controller.onShare}
                        >
                            {t('visit-access.success-share-qr')}
                        </Button>
                        <Button type={'link'} className={'va-success-secondary'} onClick={onCopy}>
                            {copied ? t('visit-access.success-copied') : t('visit-access.success-copy-link')}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};
